import { ActivationAttempt, GameState, Player, PreparedCard, CardType, Card, CardColor, GamePhase, EffectResult, DetailedMatchResult } from '../types'
import { getEffectiveEssenceCostForPlayer } from './gameLogic'
import { getCardEffect, isImmuneToDamage, checkAndTriggerLifeCharger, isShieldEffect, applyHealthLoss } from './effects'
import { ensureDealingDeck, drawFromDealingDeck, shuffle } from './decks'
import { generateTowerId } from './towers'
import { isValidCounter } from './ai'
import { xmur3, mulberry32 } from './random'
import { getEffectiveHero } from './heroes'
import { buildEffectResult, applyEffectPriorities } from './effect_resolution'
import { calculateFamePoints, FHGame } from './fhCalculator'
import { getValidCounterblastActivationIds, isImmediateDamageSource, isSpikesCard } from './damageRules'

export const getTopActivation = (state: GameState): ActivationAttempt | undefined => {
  // Find the last unresolved activation (LIFO - last in, first out)
  for (let i = state.activations.length - 1; i >= 0; i--) {
    if (!state.activations[i].resolved) {
      return state.activations[i]
    }
  }
  return undefined
}

export const attemptActivation = (state: GameState, playerId: number, cardId: string, rotation: number, targetId?: number): GameState => {
  // FIX: Check for unresolved activations, not just array length (resolved activations should not block new plays)
  const hasUnresolvedActivation = state.activations.some(a => !a.resolved)
  if (state.turnPlayerId !== playerId || hasUnresolvedActivation) return state
  const playerIndex = state.players.findIndex(p => p.id === playerId)
  if (playerIndex === -1) return state
  const player = state.players[playerIndex]
  let preparedCardData = player.prepared.find(pc => pc.card.id === cardId)
  if (!preparedCardData) {
    if (player.isAI) {
      return passTurn(state, playerId)
    }
    return state
  }
  if (preparedCardData.card.type === CardType.PlantedBomb) {
    return { ...state, log: [...state.log, `t:log.cannotUseDirect|playerName=${player.name}|cardName=Planted Bomb`] }
  }
  let preparedCard: PreparedCard = { ...preparedCardData, rotation }
  console.log(`[attemptActivation DEBUG] cardId=${cardId}, rotation param=${rotation}, preparedCard.rotation=${preparedCard.rotation}, preparedCardData.rotation=${preparedCardData.rotation}`)
  const playersForUpdate: [Player, Player] = JSON.parse(JSON.stringify(state.players))
  const effect = getCardEffect(preparedCard)
  if (player.hasPassedActiveTurn && !isShieldEffect(effect.type) && !preparedCard.card.playableAfterPass) {
    if (player.isAI) {
      return passTurn(state, playerId)
    }
    return { ...state, log: [...state.log, `t:log.shieldOnly|playerName=${player.name}`] }
  }
  if (preparedCardData.lockedState) {
    const roundsSinceLocked = state.round - preparedCardData.lockedState.appliedRound
    if (roundsSinceLocked === 0) {
      if (player.isAI) {
        return passTurn(state, playerId)
      }
      return { ...state, log: [...state.log, `t:log.lockedCard|playerName=${player.name}`] }
    }
    preparedCard = { ...preparedCardData, rotation: preparedCardData.rotation }
  }
  // Seal of Silence: block Pink color deck and Spell stash cards for the silenced player during this night
  const silencedNow = player.effectCards.some(c => c.type === CardType.SealOfSilence && c.appliedRound === state.round)
  if (silencedNow) {
    const isPinkColorDeck = !preparedCard.card.essenceCost && preparedCard.card.color === CardColor.Pink
    const isSpellStash = !!preparedCard.card.essenceCost && preparedCard.card.subType === 'Spell'
    if (isPinkColorDeck || isSpellStash) {
      if (player.isAI) {
        return passTurn(state, playerId)
      }
      return { ...state, log: [...state.log, `t:log.silenced|playerName=${player.name}|cardType=${isSpellStash ? 'Spell' : 'Pink'}`] }
    }
  }
  if (effect.type === CardType.StealTower && player.capturedTowerThisTurn) {
    if (player.isAI) {
      return passTurn(state, playerId)
    }
    return { ...state, log: [...state.log, `t:log.alreadyCaptured|playerName=${player.name}`] }
  }
  const newPlayers: [Player, Player] = playersForUpdate
  const attacker = newPlayers.find(p => p.id === playerId)!
  // FIX: Update rotation in prepared slot so cleanup logic uses correct card type
  const preparedIdx = attacker.prepared.findIndex(pc => pc.card.id === cardId)
  if (preparedIdx !== -1) {
    attacker.prepared[preparedIdx] = { ...attacker.prepared[preparedIdx], rotation }
  }
  let newLog = [...state.log]
  const isStashCard = !!preparedCard.card.essenceCost
  if (isStashCard) {
    const effectiveCost = getEffectiveEssenceCostForPlayer(preparedCard.card, attacker)
    if (attacker.essence < effectiveCost) {
      if (attacker.isAI) {
        return passTurn(state, playerId)
      }
      return { ...state, log: [...newLog, `t:log.notEnoughEssence|playerName=${attacker.name}`] }
    }
    attacker.essence -= effectiveCost
    newLog.push(`t:log.payEssence|playerName=${attacker.name}|essence=${effectiveCost}`)
  }
  const newActivation: ActivationAttempt = {
    initiatorId: playerId,
    attackingCard: preparedCard,
    counterChain: [],
    state: 'PENDING_RESPONSE',
    targetId: targetId ?? (playerId === 1 ? 2 : 1),
    resolved: false,
    accepted: false,
    // PHASE 1: Primary activations are always root nodes
    parentActivationIndex: undefined,
    childActivationIndices: [],
    treeDepth: 0,
    triggeredCards: []
  }
  newLog.push(`t:log.attemptActivate|playerName=${attacker.name}|cardType=${preparedCard.card.faceSlug || getCardEffect(preparedCard).type}`)
  return { ...state, players: newPlayers, log: newLog, activations: [newActivation], consecutivePasses: 0, lastCardPlayerId: playerId }
}

export const attemptShieldActivation = (state: GameState, playerId: number, cardId: string, rotation: number, targetActivationId?: string): GameState => {
  if (state.activations.length === 0) {
    return state
  }

  // GLOBAL TURN ORDER CHECK: Strict alternation P1 → P2 → P1 → P2
  // If I played the last card globally, I MUST wait for opponent's response
  if (state.lastCardPlayerId !== undefined && state.lastCardPlayerId === playerId) {
    return state
  }

  const playerIndex = state.players.findIndex(p => p.id === playerId)
  if (playerIndex === -1) return state
  const player = state.players[playerIndex]
  const preparedCardData = player.prepared.find(pc => pc.card.id === cardId)
  if (!preparedCardData || !preparedCardData.card.extendedShield) {
    return state
  }
  // Seal of Silence: block Spell-type stash shields for the silenced player during this night
  const silencedForShield = player.effectCards.some(c => c.type === CardType.SealOfSilence && c.appliedRound === state.round)
  if (silencedForShield && preparedCardData.card.subType === 'Spell') {
    return { ...state, log: [...state.log, `t:log.silenced|playerName=${player.name}|cardType=Spell`] }
  }
  const cardBeingActivated = preparedCardData.card
  const specialShieldsToCheck = [CardType.Protector, CardType.Counterblast, CardType.LifeCharger, CardType.Invincible]
  const isSpecialShield = specialShieldsToCheck.includes(cardBeingActivated.type) || (cardBeingActivated.type === CardType.Defense && cardBeingActivated.subType === 'Armory')

  // Seal of Silence validation: can only target Pink color deck cards or Spell stash cards
  // Logic for target selection: if last card matches, add to existing chain via counterActivation
  // Otherwise create new branch targeting first card
  if (cardBeingActivated.type === CardType.SealOfSilence) {
    if (state.activations.length === 0) {
      // No active chains - SealOfSilence can be played as a direct action (applies silence effect)
      // This is allowed - continue to create new activation
    } else {
      const parentActivation = state.activations[state.activations.length - 1]
      const firstCard = state.activations[0].attackingCard.card
      const lastCard = parentActivation.counterChain.length > 0
        ? parentActivation.counterChain[parentActivation.counterChain.length - 1].card
        : parentActivation.attackingCard.card

      const isFirstPink = !firstCard.essenceCost && firstCard.color === CardColor.Pink
      const isFirstSpell = !!firstCard.essenceCost && firstCard.subType === 'Spell'
      const isLastPink = !lastCard.essenceCost && lastCard.color === CardColor.Pink
      const isLastSpell = !!lastCard.essenceCost && lastCard.subType === 'Spell'

      const firstValid = isFirstPink || isFirstSpell
      const lastValid = isLastPink || isLastSpell

      if (!firstValid && !lastValid) {
        return { ...state, log: [...state.log, `t:log.silencePinkSpell|playerName=${player.name}`] }
      }

      // If last card is valid target, add to existing chain via counterActivation
      if (lastValid) {
        return counterActivation(state, playerId, cardId, rotation)
      }
      // Otherwise, first card is valid - continue to create new branch targeting first card
    }
  }

  // Planted Bomb validation: can only target Stash Deck cards belonging to opponent
  // Logic: if last card is valid (Stash Deck, opponent's), add to chain via counterActivation
  // Otherwise create new branch targeting first valid Stash card
  if (cardBeingActivated.type === CardType.PlantedBomb) {
    if (state.activations.length === 0) {
      // PlantedBomb cannot be played without active chains
      return { ...state, log: [...state.log, `t:log.plantedBombChain|playerName=${player.name}`] }
    }

    const parentActivation = state.activations[state.activations.length - 1]
    const firstCard = state.activations[0].attackingCard
    const lastCard = parentActivation.counterChain.length > 0
      ? parentActivation.counterChain[parentActivation.counterChain.length - 1]
      : parentActivation.attackingCard

    const isFirstStash = !!firstCard.card.essenceCost && firstCard.card.ownerId !== playerId
    const isLastStash = !!lastCard.card.essenceCost && lastCard.card.ownerId !== playerId

    if (!isFirstStash && !isLastStash) {
      return { ...state, log: [...state.log, `t:log.plantedBombStash|playerName=${player.name}`] }
    }

    // If last card is valid target (opponent's Stash card), add to existing chain
    if (isLastStash) {
      return counterActivation(state, playerId, cardId, rotation)
    }
    // Otherwise, first card is valid - continue to create new branch
  }

  // Counterblast validation: can only target damage-dealing cards belonging to opponent
  // Logic: if last card is valid (damage-dealing, opponent's), add to chain via counterActivation
  // Otherwise create new branch targeting first valid damage card
  if (cardBeingActivated.type === CardType.Counterblast) {
    const validCounterblastIds = getValidCounterblastActivationIds(state, playerId)
    if (!targetActivationId || !validCounterblastIds.includes(targetActivationId)) {
      return { ...state, log: [...state.log, `t:log.defenseActionOnly|playerName=${player.name}|cardName=Counterblast`] }
    }
    if (false) {
    if (state.activations.length === 0) {
      // Counterblast requires an active damage-dealing card to target — cannot be played without one
      return { ...state, log: [...state.log, `t:log.defenseActionOnly|playerName=${player.name}|cardName=Counterblast`] }
    }

    const parentActivation = state.activations[state.activations.length - 1]
      const firstCard = state.activations[0].attackingCard
      const lastCard = parentActivation.counterChain.length > 0
        ? parentActivation.counterChain[parentActivation.counterChain.length - 1]
        : parentActivation.attackingCard

      const damageDealingTypes = [CardType.Attack, CardType.EnergySword, CardType.FlyingScythe, CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain, CardType.Chromabow, CardType.Trembling]

      const firstEffect = getCardEffect(firstCard)
      const lastEffect = getCardEffect(lastCard)

      // Check if Protector is flipped (damage mode)
      const firstRotation = (firstCard.rotation % 360 + 360) % 360
      const firstFlipped = firstRotation > 90 && firstRotation < 270
      const isFirstDamage = (damageDealingTypes.includes(firstEffect.type) || (firstEffect.type === CardType.Protector && firstFlipped)) && firstCard.card.ownerId !== playerId

      const lastRotation = (lastCard.rotation % 360 + 360) % 360
      const lastFlipped = lastRotation > 90 && lastRotation < 270
      const isLastDamage = (damageDealingTypes.includes(lastEffect.type) || (lastEffect.type === CardType.Protector && lastFlipped)) && lastCard.card.ownerId !== playerId

      if (!isFirstDamage && !isLastDamage) {
        return { ...state, log: [...state.log, `t:log.defenseActionOnly|playerName=${player.name}|cardName=Counterblast`] }
      }

      // If last card is valid target (opponent's damage card), add to existing chain
      if (isLastDamage) {
        return counterActivation(state, playerId, cardId, rotation)
      }
      // Otherwise, first card is valid - continue to create new branch (handled by isSpecialShield)
    }
  }


  if (isSpecialShield) {
    const originalActivation = targetActivationId
      ? state.activations.find(act => act.attackingCard.card.id === targetActivationId) || state.activations[0]
      : state.activations[0]
    const targetCard = originalActivation.attackingCard
    const targetEffect = getCardEffect(targetCard)

    // Invincible can counter Natural Grip and Disease, but Ancient Shield/Protector cannot
    const isTargetDamageDealing = isImmediateDamageSource(targetCard)
    const isInvincibleDOTCounter = cardBeingActivated.type === CardType.Invincible && (targetEffect.type === CardType.NaturalGrip || targetEffect.type === CardType.Disease)

    if (cardBeingActivated.type !== CardType.Counterblast && !isTargetDamageDealing && !isInvincibleDOTCounter) {
      const cardName = cardBeingActivated.type === CardType.Defense && cardBeingActivated.subType === 'Armory' ? 'Ancient Shield' : cardBeingActivated.type
      return { ...state, log: [...state.log, `t:log.defenseActionOnly|playerName=${player.name}|cardName=${cardName}`] }
    }
  }
  let preparedCard: PreparedCard = { ...preparedCardData, rotation }
  const playersForUpdate: [Player, Player] = JSON.parse(JSON.stringify(state.players))
  const newPlayers: [Player, Player] = playersForUpdate
  const attacker = newPlayers.find(p => p.id === playerId)!
  // FIX: Update rotation in prepared slot so cleanup logic uses correct card type
  const preparedIdx = attacker.prepared.findIndex(pc => pc.card.id === cardId)
  if (preparedIdx !== -1) {
    attacker.prepared[preparedIdx] = { ...attacker.prepared[preparedIdx], rotation }
  }
  let newLog = [...state.log]
  const { type } = getCardEffect(preparedCard)
  // If Shimmering Shield, dynamically adjust defense value based on missing HP before resolution
  if (preparedCard.card.subType === 'Armory' && ((preparedCard.card as any).faceSlug === 'shimmering-shield' || (preparedCard.card.extendedShield && preparedCard.card.value === 2 && preparedCard.card.essenceCost === 5))) {
    const missing = attacker.maxHealth - attacker.health
    const bonus = Math.floor(Math.max(0, missing) / 2)
    const computedDefense = 2 + bonus
    preparedCard = { ...preparedCard, card: { ...preparedCard.card, value: computedDefense } }
  }
  if (preparedCard.card.essenceCost) {
    const effectiveCost = getEffectiveEssenceCostForPlayer(preparedCard.card, attacker)
    if (attacker.essence < effectiveCost) {
      return { ...state, log: [...newLog, `t:log.notEnoughEssence|playerName=${attacker.name}`] }
    }
    attacker.essence -= effectiveCost
    newLog.push(`t:log.payEssence|playerName=${attacker.name}|essence=${effectiveCost}`)
  }
  // PHASE 1: Find parent activation (the card this shield is reacting to)
  // Use targetActivationId if provided (look up by card ID), otherwise default to most recent activation
  let parentIndex = state.activations.length - 1
  if (targetActivationId !== undefined) {
    const foundIndex = state.activations.findIndex(act => act.attackingCard.card.id === targetActivationId)
    if (foundIndex >= 0) {
      parentIndex = foundIndex
    } else {
    }
  }
  const parentActivation = parentIndex >= 0 && parentIndex < state.activations.length ? state.activations[parentIndex] : undefined

  const newActivation: ActivationAttempt = {
    initiatorId: playerId,
    attackingCard: preparedCard,
    counterChain: [],
    state: 'PENDING_RESPONSE',
    targetId: playerId === 1 ? 2 : 1,
    resolved: false,
    accepted: false,
    // PHASE 1: Parent-child tracking
    parentActivationIndex: parentIndex >= 0 ? parentIndex : undefined,
    childActivationIndices: [],
    treeDepth: parentActivation ? (parentActivation.treeDepth || 0) + 1 : 0,
    triggeredCards: []
  }

  newLog.push(`t:log.activateCard|playerName=${attacker.name}|cardType=${preparedCard.card.faceSlug || getCardEffect(preparedCard).type}`)

  // PHASE 1: Update parent's children list
  const newActivations = [...state.activations]
  if (parentIndex >= 0 && parentActivation) {
    newActivations[parentIndex] = {
      ...parentActivation,
      childActivationIndices: [
        ...(parentActivation.childActivationIndices || []),
        newActivations.length  // Index of the new activation we're about to add
      ]
    }
  }
  newActivations.push(newActivation)

  return { ...state, players: newPlayers, log: newLog, activations: newActivations, consecutivePasses: 0, lastCardPlayerId: playerId }
}

export const counterActivation = (state: GameState, responderId: number, counterCardId: string, rotation: number, targetActivationId?: string): GameState => {
  // GLOBAL TURN ORDER CHECK: Strict alternation P1 → P2 → P1 → P2
  // If I played the last card globally, I MUST wait for opponent's response  
  if (state.lastCardPlayerId !== undefined && state.lastCardPlayerId === responderId) {
    console.log(`[ACT-DEBUG] counterActivation REJECTED: lastCardPlayerId (${state.lastCardPlayerId}) === responderId (${responderId})`);
    return state
  }

  // PHASE 5: Determine which activation to counter using card ID (stable across clients)
  let targetActivation: ActivationAttempt | undefined
  let targetIndex: number

  if (targetActivationId !== undefined) {
    // Player specified which activation to counter by card ID
    const foundIndex = state.activations.findIndex(act => act.attackingCard.card.id === targetActivationId)
    if (foundIndex < 0) {
      return state
    }
    targetActivation = state.activations[foundIndex]
    targetIndex = foundIndex

    // PHASE 5: Validate can't counter resolved activation
    if (targetActivation.resolved) {
      console.log(`[ACT-DEBUG] counterActivation REJECTED: Target activation resolved`);
      return state
    }
  } else {
    // Default behavior: counter top unresolved activation (backwards compatible)
    targetActivation = getTopActivation(state)
    if (!targetActivation) {
      console.log(`[ACT-DEBUG] counterActivation REJECTED: No top activation found`);
      return state
    }
    targetIndex = state.activations.findIndex(act => act === targetActivation)
  }

  const allCardsInAllChains = new Set(state.activations.flatMap(act => [act.attackingCard.card.id, ...act.counterChain.map(pc => pc.card.id)]))
  if (allCardsInAllChains.has(counterCardId)) {
    console.log(`[ACT-DEBUG] counterActivation REJECTED: Card ${counterCardId} already in chain`);
    return state
  }

  // PER-CHAIN TARGETING: Can only add to chains where opponent played last in THAT chain
  const lastCardInTargetChain = targetActivation.counterChain.length > 0
    ? targetActivation.counterChain[targetActivation.counterChain.length - 1]
    : targetActivation.attackingCard;

  if (lastCardInTargetChain.card.ownerId === responderId) {
    console.log(`[ACT-DEBUG] counterActivation REJECTED: Target card owner (${lastCardInTargetChain.card.ownerId}) === responderId`);
    return state
  }
  const responderIndex = state.players.findIndex(p => p.id === responderId)
  if (responderIndex === -1) return state
  const responder = state.players[responderIndex]
  let cardIndexInPrepared = responder.prepared.findIndex(pc => pc.card.id === counterCardId)
  if (cardIndexInPrepared < 0) {
    console.log(`[ACT-DEBUG] counterActivation REJECTED: Card ${counterCardId} not found in prepared`);
    return state
  }
  let newPlayers: [Player, Player] = JSON.parse(JSON.stringify(state.players))
  let newResponder = newPlayers[responderIndex]
  let preparedCardData = newResponder.prepared.find(pc => pc.card.id === counterCardId)!
  let counterCard: PreparedCard = { ...preparedCardData, rotation }
  // FIX: Update rotation in prepared slot so cleanup logic uses correct card type
  const preparedIdx = newResponder.prepared.findIndex(pc => pc.card.id === counterCardId)
  if (preparedIdx !== -1) {
    newResponder.prepared[preparedIdx] = { ...newResponder.prepared[preparedIdx], rotation }
  }

  if (true) {
    console.log(`[ACT-DEBUG] counterCard ID=${counterCard.card.id} Rot=${counterCard.rotation} Type=${counterCard.card.type} TypeB=${counterCard.card.typeB} ValueB=${(counterCard.card as any).valueB}`);
    // Dump full card keys
    console.log(`[ACT-DEBUG] Card keys: ${Object.keys(counterCard.card).join(',')}`);
  }

  // Seal of Silence: block Pink color deck and Spell stash counters for the silenced player during this night
  const responderSilenced = newResponder.effectCards.some(c => c.type === CardType.SealOfSilence && c.appliedRound === state.round)
  if (responderSilenced) {
    const isPinkColorDeck = !counterCard.card.essenceCost && counterCard.card.color === CardColor.Pink
    const isSpellStash = !!counterCard.card.essenceCost && counterCard.card.subType === 'Spell'
    if (isPinkColorDeck || isSpellStash) {
      return { ...state, log: [...state.log, `${newResponder.name} is silenced and cannot play ${isSpellStash ? 'Spell' : 'Pink'} cards this night.`] }
    }
  }
  if (preparedCardData.lockedState) {
    const roundsSinceLocked = state.round - preparedCardData.lockedState.appliedRound
    if (roundsSinceLocked === 0) {
      return { ...state, log: [...state.log, `${responder.name} cannot use this locked card yet!`] }
    }
    counterCard = { ...preparedCardData, rotation: preparedCardData.rotation }
  }
  const { type: counterType } = getCardEffect(counterCard)

  if (counterType === CardType.Counterblast || isSpikesCard(counterCard)) {
    return attemptShieldActivation(state, responderId, counterCardId, rotation, targetActivationId)
  }

  // Double-Cross validation: Must be countering a reaction to your own INITIAL action
  // Find the root activation (treeDepth=0) and check if we initiated it
  if (counterType === CardType.DoubleCross) {
    const rootActivation = state.activations.find(act => act.treeDepth === 0 || act.parentActivationIndex === undefined)
    const isOurInitialAction = rootActivation && rootActivation.initiatorId === responderId

    if (!isOurInitialAction) {
      return { ...state, log: [...state.log, `Double-Cross cannot be used as an initial reaction.`] }
    }
  }
  const targetCard = targetActivation.counterChain.length > 0 ? targetActivation.counterChain[targetActivation.counterChain.length - 1] : targetActivation.attackingCard
  if (!isValidCounter(counterCard, targetCard)) {
    console.log(`[ACT-DEBUG] counterActivation REJECTED: isValidCounter returned false for ${counterCard.card.type} vs ${targetCard.card.type}`);
    return { ...state, log: [...state.log, `Invalid counter attempt.`] }
  }

  // ESSENCE COST DEDUCTION: Stash-based reaction cards (Planted Bomb, Counterblast, etc.) must pay essence
  const isStashCard = !!counterCard.card.essenceCost
  let extraLog: string[] = []
  if (isStashCard) {
    const effectiveCost = getEffectiveEssenceCostForPlayer(counterCard.card, newResponder)
    if (newResponder.essence < effectiveCost) {
      if (newResponder.isAI) {
        return state
      }
      return { ...state, log: [...state.log, `t:log.notEnoughEssence|playerName=${newResponder.name}`] }
    }
    newResponder.essence -= effectiveCost
    extraLog.push(`t:log.payEssence|playerName=${newResponder.name}|essence=${effectiveCost}`)
  }

  const finalCardIndexInPrepared = newResponder.prepared.findIndex(pc => pc.card.id === counterCardId)
  // Effect cards (Hibernation, Invincible, etc.) should stay in prepared until they're moved to effectCards
  // This preserves the animation transition from prepared → effectCards
  const effectCardTypes = [CardType.FortunesFavor, CardType.Hibernation, CardType.Disease, CardType.CursedIron, CardType.EyesOfMadness, CardType.MadnessControl, CardType.NaturalGrip, CardType.Invincible, CardType.VoodooDoll, CardType.BodyUpgrade, CardType.SealOfSilence, CardType.Frost, CardType.DiscerningEye]
  const isEffectCard = effectCardTypes.includes(counterType)
  if (!isEffectCard) {
    newResponder.prepared.splice(finalCardIndexInPrepared, 1)
  }
  const newTargetActivation = { ...targetActivation, counterChain: [...targetActivation.counterChain, counterCard], state: targetActivation.state === 'PENDING_RESPONSE' ? 'PENDING_CONFIRMATION' as const : 'PENDING_RESPONSE' as const }

  // PHASE 5: Update activation at targetIndex instead of using slice
  const newActivations = [...state.activations]
  newActivations[targetIndex] = newTargetActivation

  return { ...state, players: newPlayers, activations: newActivations, log: [...state.log, ...extraLog, `t:log.counterWith|playerName=${newResponder.name}|cardType=${counterCard.card.faceSlug || getCardEffect(counterCard).type}`], lastCardPlayerId: responderId }
}

export const acceptActivation = (state: GameState, acceptingPlayerId: number): GameState => {
  if (state.activations.length === 0) return state

  // Check if there are ANY unaccepted activations
  const hasUnaccepted = state.activations.some(act => !act.accepted)
  if (!hasUnaccepted) return state

  // NEW: Check if player can accept ANY activation (not just the last one)
  // With multiple parallel chains, different activations can have different states
  const canAcceptAny = state.activations.some(act => {
    if (act.accepted) return false
    const initiatorId = act.initiatorId
    const responderId = initiatorId === 1 ? 2 : 1
    const isResponderAccepting = act.state === 'PENDING_RESPONSE' && acceptingPlayerId === responderId
    const isInitiatorConfirming = act.state === 'PENDING_CONFIRMATION' && acceptingPlayerId === initiatorId
    return isResponderAccepting || isInitiatorConfirming
  })

  if (!canAcceptAny) {
    return state
  }

  // KEY CHANGE: Accept closes ALL activations in the tree, not just one

  // Mark ALL activations as accepted
  const newActivations: ActivationAttempt[] = state.activations.map(act => ({
    ...act,
    accepted: true
  }));

  let newState: GameState = { ...state, activations: newActivations };

  // Immediately start resolution since all are now accepted
  newState = resolveAllActivations(newState);

  return newState;
}

const resolveAllActivations = (state: GameState): GameState => {
  let currentState = state;

  // Resolve LIFO (from last to first)
  for (let i = currentState.activations.length - 1; i >= 0; i--) {
    if (currentState.activations[i].resolved) continue;

    currentState = resolveSingleActivation(currentState, i);
  }

  return finalResolution(currentState);
}

const resolveSingleActivation = (state: GameState, activationIndex: number): GameState => {
  const currentActivation = state.activations[activationIndex]
  if (!currentActivation) return state

  // PHASE 3: Validate that this activation is not already resolved
  if (currentActivation.resolved) {
    return state
  }

  const initiatorId = currentActivation.initiatorId


  // PHASE 2: Deep copy players for counter-chain processing
  let playersCopy: [Player, Player] = JSON.parse(JSON.stringify(state.players))
  let newLog = [...state.log]
  const returnedToColors = new Set<CardColor>()
  let newColorDecks: Record<CardColor, Card[]> = JSON.parse(JSON.stringify(state.colorDecks))
  let newStashDeck: Card[] = JSON.parse(JSON.stringify(state.stashDeck))
  let stashDeckModified = false

  // Build resolution chain and track cards
  let resolutionChain: PreparedCard[] = [currentActivation.attackingCard, ...currentActivation.counterChain]
  let triggeredCards: PreparedCard[] = currentActivation.triggeredCards ? [...currentActivation.triggeredCards] : []

  // SEAL OF SILENCE PRE-PROCESSING:
  // Check if any child activation is a resolved SealOfSilence targeting a Pink/Spell card in our chain.
  // If so, remove that card from our chain BEFORE processing (it was returned to hand).
  const childIndices = currentActivation.childActivationIndices || []
  for (const childIdx of childIndices) {
    const childActivation = state.activations[childIdx]
    if (!childActivation || !childActivation.resolved) continue
    if (!childActivation.winningCard) continue

    const childCardType = getCardEffect(childActivation.winningCard).type
    if (childCardType !== CardType.SealOfSilence) continue

    // SealOfSilence was played as reaction. Find the last card in our chain (the target).
    if (resolutionChain.length > 0) {
      const lastCardInChain = resolutionChain[resolutionChain.length - 1]
      const targetCardData = lastCardInChain.card
      const isPinkColorDeck = !targetCardData.essenceCost && targetCardData.color === CardColor.Pink
      const isSpellStash = !!targetCardData.essenceCost && targetCardData.subType === 'Spell'

      if (isPinkColorDeck || isSpellStash) {
        // Remove it from our resolution chain
        resolutionChain = resolutionChain.slice(0, -1)
      }
    }
  }
  const allChainCards = [...resolutionChain]
  const usedCardIds = new Set<string>(allChainCards.map(pc => pc.card.id))
  const psyBlockedCardIds = new Set<string>()
  const banishedCards: PreparedCard[] = []
  const eliminatedCards: PreparedCard[] = []
  let newEliminatedCards = [...state.eliminatedCards]
  let partiallyCounteredCard: PreparedCard | undefined = undefined  // Card whose damage was prevented but secondary effects should still apply

  // Track damage attempts for statistics
  const attemptedPre: Record<string, number> = { ...(state.attemptedDamageCardsThisNight || {}) }
  const attemptedMap: Record<string, number> = { ...attemptedPre }
  const incAttempt = (attId: number, defId: number) => {
    const key = `${attId}->${defId}`
    attemptedMap[key] = (attemptedMap[key] || 0) + 1
  }

  // PHASE 2: COUNTER-CHAIN PROCESSING ONLY
  // Process counter-chain from top to bottom
  while (resolutionChain.length >= 2) {
    const counterCard = resolutionChain.pop()!
    const targetCard = resolutionChain.pop()!
    const counterEffect = getCardEffect(counterCard)
    const targetEffect = getCardEffect(targetCard)
    const counterOwner = playersCopy.find(p => p.id === counterCard.card.ownerId)!
    const targetOwner = playersCopy.find(p => p.id === targetCard.card.ownerId)!

    // Track damage attempts
    {
      const finalRotation = (targetCard.rotation % 360 + 360) % 360
      const isFlipped = finalRotation > 90 && finalRotation < 270
      const damageDealingTypes = [CardType.Attack, CardType.EnergySword, CardType.FlyingScythe, CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain, CardType.Chromabow, CardType.Trembling, CardType.Disease]
      const isTargetDamageDealingAttempt = damageDealingTypes.includes(targetEffect.type) || (targetEffect.type === CardType.Protector && isFlipped)
      if (isTargetDamageDealingAttempt) incAttempt(targetOwner.id, counterOwner.id)
    }

    // Process counter interactions
    if (counterEffect.type === CardType.Pushback) {
      const counterEffectiveHero = getEffectiveHero(counterOwner, targetOwner)
      if (counterEffectiveHero === 'CORIL' && targetCard.card.subType === 'Armory') {
        targetCard.card.ownerId = counterOwner.id
        counterOwner.hand.push(targetCard.card)
        newLog.push(`${counterOwner.name}'s Pushback (Coril) steals ${targetOwner.name}'s Armory!`)
      } else {
        targetOwner.hand.push(targetCard.card)
        newLog.push(`${counterOwner.name}'s Pushback returns ${targetOwner.name}'s ${targetEffect.type} to their hand.`)
      }
      // Also remove the card from prepared if it was kept there as an effect card
      // (effect cards are kept in prepared for animation purposes, but Pushback returns them to hand)
      const prepIdx = targetOwner.prepared.findIndex(pc => pc.card.id === targetCard.card.id)
      if (prepIdx !== -1) {
        targetOwner.prepared.splice(prepIdx, 1)
      }
    } else if (counterEffect.type === CardType.PsyBlock) {
      const counterEffectiveHero = getEffectiveHero(counterOwner, targetOwner)
      if (counterEffectiveHero === 'CORIL' && targetCard.card.subType === 'Artifact') {
        targetCard.card.ownerId = counterOwner.id
        counterOwner.hand.push(targetCard.card)
        newLog.push(`${counterOwner.name}'s Psy-Block (Coril) steals ${targetOwner.name}'s Artifact!`)
        // Remove the stolen card from opponent's prepared slots
        const prepIdx = targetOwner.prepared.findIndex(pc => pc.card.id === targetCard.card.id)
        if (prepIdx !== -1) {
          targetOwner.prepared.splice(prepIdx, 1)
        }
      } else {
        const existingIndex = targetOwner.prepared.findIndex(pc => pc.card.id === targetCard.card.id)
        const lockedCard: PreparedCard = { ...targetCard, lockedState: { appliedRound: state.round } }
        if (existingIndex !== -1) {
          targetOwner.prepared[existingIndex] = lockedCard
        } else {
          targetOwner.prepared.push(lockedCard)
        }
        psyBlockedCardIds.add(targetCard.card.id)
        newLog.push(`${counterOwner.name}'s Psy-Block locks ${targetOwner.name}'s ${targetEffect.type} in place!`)
      }
    } else if (
      counterEffect.type === CardType.Counterblast &&
      targetEffect.type === CardType.Defense &&
      targetCard.card.subType === 'Armory' &&
      ((targetCard.card as any).faceSlug === 'spikes' || (targetCard.card.extendedShield && targetCard.card.value === 0 && targetCard.card.essenceCost === 6))
    ) {
    } else if (
      counterEffect.type === CardType.Defense &&
      counterCard.card.subType === 'Armory' &&
      ((counterCard.card as any).faceSlug === 'spikes' || (counterCard.card.extendedShield && counterCard.card.value === 0 && counterCard.card.essenceCost === 6))
    ) {
      triggeredCards.push(counterCard)
      resolutionChain.push(targetCard)
    } else if (
      counterEffect.type === CardType.Defense &&
      counterCard.card.subType === 'Armory' &&
      (
        (counterCard.card as any).faceSlug === 'shimmering-shield' ||
        (counterCard.card.extendedShield && counterCard.card.value === 2 && counterCard.card.essenceCost === 5)
      )
    ) {
      const missing = counterOwner.maxHealth - counterOwner.health
      const bonus = Math.floor(missing / 2)
      const computedDefense = 2 + Math.max(0, bonus)
      counterOwner.shield += computedDefense
      newLog.push(`${counterOwner.name}'s Shimmering Shield grants ${computedDefense} defense.`)
      resolutionChain.push(targetCard)
    } else if (counterEffect.type === CardType.Defense && targetEffect.type === CardType.Attack) {
      const attackValue = targetEffect.value
      const defenseValue = counterEffect.value
      if (defenseValue >= attackValue) {
        newLog.push(`${counterOwner.name}'s ${defenseValue} Defense fully blocks the ${attackValue} Attack.`)
      } else {
        newLog.push(`${counterOwner.name}'s ${defenseValue} Defense partially blocks the ${attackValue} Attack.`)
        resolutionChain.push({ ...targetCard, card: { ...targetCard.card, value: attackValue - defenseValue } })
      }
    } else if (counterEffect.type === CardType.Defense && targetEffect.type === CardType.Counterblast) {
      // Defense blocks Counterblast's reflected damage
      const defenseValue = counterEffect.value || 0
      newLog.push(`${counterOwner.name}'s ${defenseValue} Defense blocks the Counterblast from ${targetOwner.name}.`)
      // Counterblast is fully blocked - no damage reflection occurs
    } else if (counterEffect.type === CardType.Invincible && targetEffect.type === CardType.Attack) {
      newLog.push(`${counterOwner.name}'s Invincible absorbs the ${targetEffect.value} Attack from ${targetOwner.name}.`)
    } else if (counterEffect.type === CardType.Hibernation) {
      // Hibernation counters ALL damage-dealing cards (not just Attack)
      // Hibernation PREVENTS damage but does NOT cancel the card's other effects
      const damageTypes = [
        CardType.Attack, CardType.EnergySword, CardType.FlyingScythe,
        CardType.InfernoGauntlet, CardType.Larcendagger, CardType.LifeDrain,
        CardType.Chromabow, CardType.Trembling, CardType.Disease,
        CardType.Counterblast, CardType.NaturalGrip
      ]
      const isSpikesCard = targetEffect.type === CardType.Defense &&
        targetCard.card.subType === 'Armory' &&
        ((targetCard.card as any).faceSlug === 'spikes' ||
          (targetCard.card.extendedShield && targetCard.card.value === 0 && targetCard.card.essenceCost === 6))

      // Protector в Attack Mode (перевёрнут на 180°) тоже наносит урон
      const targetRotation = (targetCard.rotation % 360 + 360) % 360
      const isTargetFlipped = targetRotation > 90 && targetRotation < 270
      const isProtectorAttack = targetEffect.type === CardType.Protector && isTargetFlipped

      if (damageTypes.includes(targetEffect.type) || isSpikesCard || isProtectorAttack) {
        newLog.push(`${counterOwner.name}'s Hibernation absorbs the incoming damage from ${targetOwner.name}'s ${targetEffect.type}.`)
        // Store the target card as partiallyCountered - its secondary effects should still run
        // Cards like Chromabow should still show the draw choice even though damage is prevented
        partiallyCounteredCard = targetCard
        // Push Hibernation as the winning card
        resolutionChain.push(counterCard)
      }
    } else if (counterEffect.type === CardType.SealOfSilence) {
      const isPinkColorDeck = !targetCard.card.essenceCost && targetCard.card.color === CardColor.Pink
      const isSpellStash = !!targetCard.card.essenceCost && targetCard.card.subType === 'Spell'

      if (isPinkColorDeck || isSpellStash) {
        // 1. Return target to hand
        targetOwner.hand.push(targetCard.card)
        const prepIdx = targetOwner.prepared.findIndex(pc => pc.card.id === targetCard.card.id)
        if (prepIdx !== -1) {
          targetOwner.prepared.splice(prepIdx, 1)
        }

        // 2. Apply Silence Effect to Target Owner
        // Note: effect goes to targetOwner (the one who played the countered card)
        const silenceMarker: Card = { ...counterCard.card, type: CardType.SealOfSilence, appliedRound: state.round, ownerId: counterOwner.id }
        targetOwner.effectCards.push(silenceMarker)
        newLog.push(`${counterOwner.name}'s Seal of Silence returns ${targetOwner.name}'s ${targetEffect.type} to hand and silences them!`)

        // 3. Manually remove SealOfSilence from CounterOwner's prepared 
        // (because generic cleanup skips it due to isEffectCard check)
        const counterPrepIdx = counterOwner.prepared.findIndex(pc => pc.card.id === counterCard.card.id)
        if (counterPrepIdx !== -1) {
          counterOwner.prepared.splice(counterPrepIdx, 1)
        }

        // 4. Do NOT push counterCard back. It is consumed.
      } else {
        // If target is invalid (shouldn't happen with proper validation),
        // we bounce the counterCard back to let it resolve normally (or fail) against next card?
        // But preventing "friendly fire" is hard.
        // Assuming validation prevents this.
        // If we do nothing here, both are popped and gone.
      }
    } else if (counterEffect.type === CardType.Dispel) {
      const counterEffectiveHero = getEffectiveHero(counterOwner, targetOwner)
      if (counterEffectiveHero === 'CORIL' && targetCard.card.subType === 'Spell') {
        targetCard.card.ownerId = counterOwner.id
        counterOwner.hand.push(targetCard.card)
        newLog.push(`${counterOwner.name}'s Dispel (Coril) steals ${targetOwner.name}'s Spell!`)
        // Remove the stolen card from opponent's prepared slots
        const prepIdx = targetOwner.prepared.findIndex(pc => pc.card.id === targetCard.card.id)
        if (prepIdx !== -1) {
          targetOwner.prepared.splice(prepIdx, 1)
        }
      } else {
        banishedCards.push(targetCard)
        newLog.push(`${counterOwner.name}'s Dispel banishes ${targetOwner.name}'s ${targetEffect.type}.`)
      }
    } else if (counterEffect.type === CardType.DoubleCross) {
      eliminatedCards.push(targetCard)
      eliminatedCards.push(counterCard)
      newLog.push(`${counterOwner.name}'s Double-Cross eliminates ${targetOwner.name}'s ${targetEffect.type}.`)
    } else if (counterEffect.type === CardType.PlantedBomb) {
      // PlantedBomb survives in the triggeredCards list to deal its damage, while the target card continues resolving
      newLog.push(`${counterOwner.name}'s Planted Bomb detonates against ${targetOwner.name}'s ${targetEffect.type}!`)
      triggeredCards.push(counterCard)
      resolutionChain.push(targetCard)
    }
  }

  // PHASE 2: Store winning card (if any) for later effect application in finalResolution
  const winningCard = resolutionChain.length > 0 ? resolutionChain[0] : undefined

  // PHASE 2: Card cleanup logic
  // Remove used cards from prepared slots and return them to appropriate decks
  // NOTE: Effect cards (Hibernation, Invincible, etc.) stay in prepared until they are
  // moved to effectCards in finalResolution - this preserves the animation transition
  const effectCardTypes = [CardType.FortunesFavor, CardType.Hibernation, CardType.Disease, CardType.CursedIron, CardType.EyesOfMadness, CardType.MadnessControl, CardType.NaturalGrip, CardType.Invincible, CardType.VoodooDoll, CardType.BodyUpgrade, CardType.SealOfSilence, CardType.Frost, CardType.DiscerningEye]

  playersCopy.forEach(player => {
    player.prepared = player.prepared.filter(pc => {
      const wasUsed = usedCardIds.has(pc.card.id)
      const wasPsyBlocked = psyBlockedCardIds.has(pc.card.id)
      const isBanished = banishedCards.some(c => c.card.id === pc.card.id)
      const isEliminated = eliminatedCards.some(c => c.card.id === pc.card.id)
      const isEffectCard = effectCardTypes.includes(getCardEffect(pc).type)
      if (!wasUsed) return true
      if (wasPsyBlocked) return true
      // Banished or eliminated cards must be removed, even if they are Effect Cards
      if (isBanished || isEliminated) return false
      // Keep effect cards in prepared - they'll be moved to effectCards in finalResolution
      if (isEffectCard) return true
      return false
    })
    const cardsUsedByThisPlayer = allChainCards.filter(rc => rc.card.ownerId === player.id)
    cardsUsedByThisPlayer.forEach(resolvedCard => {
      if (psyBlockedCardIds.has(resolvedCard.card.id)) return
      if (player.hand.some(c => c.id === resolvedCard.card.id)) return
      if (eliminatedCards.some(c => c.card.id === resolvedCard.card.id)) {
        newEliminatedCards.push(resolvedCard.card)
        return
      }
      if (banishedCards.some(c => c.card.id === resolvedCard.card.id)) {
        if (resolvedCard.card.subType === 'Spell' && resolvedCard.card.essenceCost) {
          // Banished Stash Spells go back to stash deck
          newStashDeck.push(resolvedCard.card)
          stashDeckModified = true
        } else if (!resolvedCard.card.essenceCost) {
          // Banished color deck cards go back to their color deck
          newColorDecks[resolvedCard.card.color].push(resolvedCard.card)
          returnedToColors.add(resolvedCard.card.color)
        }
        return
      }

      const cardEffect = getCardEffect(resolvedCard)
      // Effect cards stay on field - NOT handled here anymore, will be in finalResolution
      const isEffectCard = [CardType.FortunesFavor, CardType.Hibernation, CardType.Disease, CardType.CursedIron, CardType.EyesOfMadness, CardType.MadnessControl, CardType.NaturalGrip, CardType.Invincible, CardType.VoodooDoll, CardType.BodyUpgrade].includes(cardEffect.type)
      if (isEffectCard) {
        return // Will be handled by applyCardEffect in finalResolution
      }

      // Return card to appropriate deck
      if (resolvedCard.card.essenceCost) {
        if (resolvedCard.mageHandCast) {
          // Mage-cast cards go to stash discard (будет обработано в finalResolution)
        } else {
          player.hand.push(resolvedCard.card)
        }
        return
      }
      newColorDecks[resolvedCard.card.color].push(resolvedCard.card)
      returnedToColors.add(resolvedCard.card.color)
    })
  })

  // Shuffle returned color decks
  returnedToColors.forEach(color => {
    newColorDecks[color] = shuffle(newColorDecks[color], state.seed + `_accept_shuffle_${color}_` + state.log.length)
  })

  // Shuffle stash deck if cards were added
  if (stashDeckModified) {
    newStashDeck = shuffle(newStashDeck, state.seed + `_stash_shuffle_` + state.log.length)
  }

  // PHASE 2: Mark activation as resolved and store winningCard
  const topIndex = state.activations.findIndex(act => act === currentActivation)
  const newActivations = [...state.activations]
  const newAcceptedActivations = [...(state.acceptedActivations || [])]

  if (topIndex >= 0) {
    newActivations[topIndex] = {
      ...currentActivation,
      resolved: true,
      wasCountered: !winningCard,  // If no winning card, it was fully countered
      winningCard: winningCard,  // PHASE 2: Store winning card for finalResolution
      triggeredCards: triggeredCards, // Cards that resolve in parallel
      partiallyCounteredCard: partiallyCounteredCard  // Card whose damage was prevented but secondary effects should apply
    }
    newAcceptedActivations.push(topIndex)

    // [REWARD SHAPING] Log detailed chain resolution outcome
    const isRootActivation = currentActivation.parentActivationIndex === undefined
    const attackingCard = currentActivation.attackingCard
    const isStashCard = !!attackingCard.card.essenceCost
    const cardOwner = playersCopy.find(p => p.id === attackingCard.card.ownerId)

    // Check class bonus: Armory for Warrior, Spell for Mage, Artifact for Occultist
    let hasClassBonus = false
    if (cardOwner && isStashCard) {
      const subType = attackingCard.card.subType
      if (cardOwner.playerClass === 'WARRIOR' && subType === 'Armory') hasClassBonus = true
      if (cardOwner.playerClass === 'MAGE' && subType === 'Spell') hasClassBonus = true
      if (cardOwner.playerClass === 'OCCULTIST' && subType === 'Artifact') hasClassBonus = true
    }

    const chainType = isRootActivation ? 'ROOT' : 'REACTION'
    const cardType = isStashCard ? 'STASH' : 'COLOR'

    if (winningCard) {
      // Card resolved successfully - winner is the card's owner
      const winnerId = winningCard.card.ownerId
      const outcome = 'SUCCESS'
      newLog.push(`[REWARD] CHAIN_RESOLVED:${chainType}:${cardType}:${outcome}:PLAYER=${winnerId}:CLASS_BONUS=${hasClassBonus}`)

      // Add explicit class ability rewards
      if (hasClassBonus && cardOwner) {
        if (cardOwner.playerClass === 'WARRIOR') {
          newLog.push(`[REWARD] HERO_POWER_USED:PLAYER=${cardOwner.id}:TYPE=WARRIOR_ARMORY_BONUS`)
        } else if (cardOwner.playerClass === 'OCCULTIST') {
          newLog.push(`[REWARD] HERO_POWER_USED:PLAYER=${cardOwner.id}:TYPE=OCCULTIST_ARTIFACT_BONUS`)
        }
        // MAGE already gets reward in mageCastSpellFromHand
      }
    } else {
      // Card was fully countered - the initiator loses
      const outcome = 'FAIL'
      newLog.push(`[REWARD] CHAIN_RESOLVED:${chainType}:${cardType}:${outcome}:PLAYER=${initiatorId}:CLASS_BONUS=${hasClassBonus}`)
    }
  }

  // Return simplified state (no effect application, no choice phases)
  // IMPORTANT: Only include stashDeck if it was actually modified to avoid overwriting good state
  const baseReturn = {
    ...state,
    players: playersCopy,
    colorDecks: newColorDecks,
    log: newLog,
    activations: newActivations,
    acceptedActivations: newAcceptedActivations,
    turnPlayerId: newActivations.length > 0 ? state.turnPlayerId : initiatorId,
    consecutivePasses: 0,
    eliminatedCards: newEliminatedCards,
    attemptedDamageCardsThisNight: attemptedMap
  }

  if (stashDeckModified) {
    return { ...baseReturn, stashDeck: newStashDeck }
  }
  return baseReturn
}

import { applyCardEffect } from './activations_new_function'

// PHASE 6: Helper function to check if all activations are resolved
const allActivationsResolved = (state: GameState): boolean => {
  // No activations at all = nothing to resolve
  if (state.activations.length === 0) return false

  // All activations must have resolved flag set to true
  return state.activations.every(act => act.resolved === true)
}

// PHASE 6: Helper function to identify if a card is Spikes
const isSpikes = (preparedCard: PreparedCard): boolean => {
  const card = preparedCard.card
  return (
    card.type === CardType.Defense &&
    card.subType === 'Armory' &&
    (
      (card as any).faceSlug === 'spikes' ||
      (card.extendedShield && card.value === 0 && card.essenceCost === 6)
    )
  )
}

// PHASE 2: Tree building infrastructure
interface ActivationNode {
  index: number
  activation: ActivationAttempt
  parent: ActivationNode | null
  children: ActivationNode[]
  isActive: boolean  // !wasCountered && winningCard exists
}

// PHASE 2: Build activation tree from flat array
const buildActivationTree = (activations: ActivationAttempt[]): ActivationNode[] => {
  const nodes: ActivationNode[] = activations.map((act, idx) => ({
    index: idx,
    activation: act,
    parent: null,
    children: [],
    isActive: !act.wasCountered && !!act.winningCard
  }))

  // Link parents and children
  nodes.forEach(node => {
    if (node.activation.parentActivationIndex !== undefined) {
      const parentIndex = node.activation.parentActivationIndex
      if (parentIndex >= 0 && parentIndex < nodes.length) {
        node.parent = nodes[parentIndex]
        nodes[parentIndex].children.push(node)
      }
    }
  })


  return nodes
}

// PHASE 2: Get root nodes (activations with no parent)
const getRootNodes = (nodes: ActivationNode[]): ActivationNode[] => {
  return nodes.filter(n => n.parent === null)
}

// PHASE 3: Calculate which chains this effect can interact with
// Priority rule: Earlier child branches > Later child branches
const getAffectedChains = (node: ActivationNode, allNodes: ActivationNode[]): number[] => {
  const affected: number[] = []

  // Start from parent (or self if root)
  let current: ActivationNode | null = node.parent || node

  // Walk up to root, collecting all parent chains
  while (current) {
    affected.push(current.index)
    current = current.parent
  }

  // Include sibling chains that are earlier (lower index) than this node
  // These are affected by this node's effect due to timing
  if (node.parent) {
    node.parent.children.forEach(sibling => {
      if (sibling.index < node.index) {
        affected.push(sibling.index)
      }
    })
  }


  return affected.sort((a, b) => a - b)
}

// PHASE 3 Enhanced: Final resolution function - applies card effects in LIFO order with tree awareness
export const finalResolution = (state: GameState): GameState => {

  // LIFE CHARGER PENDING PHASE RESOLUTION
  // If we're resolving a Life Charger auto-trigger activation, check outcome
  if (state.phase === GamePhase.LifeChargerPending && state.pendingLifeChargerPlayerId !== undefined) {
    const playerId = state.pendingLifeChargerPlayerId
    const lifeChargerActivation = state.activations[0]

    if (lifeChargerActivation) {
      const wasCountered = lifeChargerActivation.wasCountered || !lifeChargerActivation.winningCard

      let playersCopy: [Player, Player] = JSON.parse(JSON.stringify(state.players))
      const playerIndex = playersCopy.findIndex(p => p.id === playerId)
      const player = playersCopy[playerIndex]
      const opponent = playersCopy.find(p => p.id !== playerId)!

      if (wasCountered) {
        // Life Charger was countered - player dies
        const loser = player
        const pendingVictory = { winnerId: opponent.id }

        return {
          ...state,
          players: playersCopy,
          phase: GamePhase.GameOver,
          winner: opponent,
          pendingVictory,
          activations: [],
          acceptedActivations: [],
          pendingLifeChargerPlayerId: undefined,
          log: [...state.log, `${loser.name}'s Life Charger was blocked! ${loser.name} has fallen!`, `${opponent.name} is victorious!`]
        }
      } else {
        // Life Charger succeeded - restore HP to max, return to hand, resume combat
        const healAmount = player.maxHealth - player.health
        player.health = player.maxHealth

        // Return Life Charger card to hand (guard against duplicate)
        const lifeChargerCard = lifeChargerActivation.winningCard!.card
        if (!player.hand.some(c => c.id === lifeChargerCard.id)) {
          player.hand.push(lifeChargerCard)
        }

        return {
          ...state,
          players: playersCopy,
          phase: GamePhase.Combat,
          activations: [],
          acceptedActivations: [],
          pendingLifeChargerPlayerId: undefined,
          turnPlayerId: playerId, // Restore turn to the healed player
          consecutivePasses: 0,   // Reset passes so combat continues normally
          log: [...state.log, `t:log.lifeChargerRestore|playerName=${player.name}|healAmount=${healAmount}`, `[REWARD] HEAL_RECEIVED:PLAYER=${player.id}:AMOUNT=${healAmount}`]
        }
      }
    }
  }

  // PHASE 2: Build activation tree
  const activationTree = buildActivationTree(state.activations)
  const rootNodes = getRootNodes(activationTree)


  let currentState = { ...state }

  // PHASE 1.5: SealOfSilence effect application
  // SealOfSilence was already processed in resolveSingleActivation (target removed from counterChain).
  // Here we apply the Silence effect and return target to hand.

  let playersCopyForSeal: [Player, Player] = JSON.parse(JSON.stringify(currentState.players))
  let newLogForSeal = [...currentState.log]
  const newActivations = [...currentState.activations]

  for (let i = 0; i < newActivations.length; i++) {
    const activation = newActivations[i]

    // Check if this is a SealOfSilence activation that won
    if (activation.winningCard && getCardEffect(activation.winningCard).type === CardType.SealOfSilence) {
      // Already processed flag: mark when effect has been applied
      if ((activation as any)._sealEffectApplied) continue

      // Check if it has a parent (was used as reaction, not proactive)
      if (activation.parentActivationIndex !== undefined && activation.parentActivationIndex >= 0) {
        const parentIndex = activation.parentActivationIndex
        const parentActivation = state.activations[parentIndex] // Use ORIGINAL state to find original counterChain

        if (parentActivation) {
          // Find the original target card (before it was removed from chain)
          const originalTargetCard = parentActivation.counterChain.length > 0
            ? parentActivation.counterChain[parentActivation.counterChain.length - 1]
            : parentActivation.attackingCard

          const targetCardData = originalTargetCard.card
          const isPinkColorDeck = !targetCardData.essenceCost && targetCardData.color === CardColor.Pink
          const isSpellStash = !!targetCardData.essenceCost && targetCardData.subType === 'Spell'

          if (isPinkColorDeck || isSpellStash) {
            const sealOwner = playersCopyForSeal.find(p => p.id === activation.winningCard!.card.ownerId)!
            const targetOwner = playersCopyForSeal.find(p => p.id === targetCardData.ownerId)!


            // 1. Return target card to owner's hand (if not already there)
            if (!targetOwner.hand.some(c => c.id === targetCardData.id)) {
              targetOwner.hand.push(targetCardData)
            }

            // 2. Remove from prepared if still there
            const prepIdx = targetOwner.prepared.findIndex(pc => pc.card.id === targetCardData.id)
            if (prepIdx !== -1) {
              targetOwner.prepared.splice(prepIdx, 1)
            }

            // 3. Apply Silence Effect to target owner (if not already applied)
            if (!targetOwner.effectCards.some(c => c.type === CardType.SealOfSilence && c.appliedRound === currentState.round)) {
              const silenceMarker: Card = {
                ...activation.winningCard!.card,
                type: CardType.SealOfSilence,
                appliedRound: currentState.round,
                ownerId: sealOwner.id
              }
              targetOwner.effectCards.push(silenceMarker)
              newLogForSeal.push(`${sealOwner.name}'s Seal of Silence returns ${targetOwner.name}'s ${getCardEffect(originalTargetCard).type} to hand and silences them!`)
            }

            // 4. Mark as processed
            newActivations[i] = {
              ...activation,
              resolved: true,
              wasCountered: false
            };
            (newActivations[i] as any)._sealEffectApplied = true
          }
        }
      }
    }
  }
  currentState = { ...currentState, activations: newActivations as ActivationAttempt[], players: playersCopyForSeal, log: newLogForSeal }



  // OPTION B: Centralized effect collection and priority resolution

  const effectResults: EffectResult[] = []

  // Collect effects from all active activations (LIFO order)
  for (let i = currentState.activations.length - 1; i >= 0; i--) {
    const activation = currentState.activations[i]

    // Skip countered activations
    if (activation.wasCountered || !activation.winningCard) {
      continue
    }

    const node = activationTree[i]
    const affectedChains = getAffectedChains(node, activationTree)

    const cardsToProcess = [activation.winningCard, ...(activation.triggeredCards || [])]

    for (const card of cardsToProcess) {
      const effectResult = buildEffectResult(
        card,
        activation,
        i,
        affectedChains,
        currentState
      )

      if (effectResult) {
        effectResults.push(effectResult)
      }
    }
  }


  // Apply tree-aware priority rules (Invincible, Counterblast, Spikes)
  applyEffectPriorities(effectResults, activationTree, currentState)


  // Execute effects in LIFO order
  // Tree-aware effects are in effectResults, others handled by applyCardEffect directly
  for (let i = currentState.activations.length - 1; i >= 0; i--) {
    const activation = currentState.activations[i]

    if (activation.wasCountered || !activation.winningCard) {
      continue
    }

    const cardsToProcess = [activation.winningCard, ...(activation.triggeredCards || [])]

    for (const currentCard of cardsToProcess) {
      const cardType = getCardEffect(currentCard).type

      // Check if this effect was handled by the tree-aware system
      const treeAwareEffect = effectResults.find(e => e.activationIndex === i && e.cardType === cardType)

      if (treeAwareEffect) {
        // Tree-aware effect - apply based on effectResults
        // Skip cancelled effects. 
        // Allow 'prevented' effects ONLY if 'damagePrevented' is true (meaning only damage is blocked, secondary effects persist)
        if (!treeAwareEffect.cancelled && (!treeAwareEffect.prevented || treeAwareEffect.damagePrevented)) {

          // Apply the tree-aware effect
          // Pass effectResult so Spikes/Counterblast can access calculated damage
          currentState = applyCardEffect(currentCard, activation, currentState, treeAwareEffect)
        } else {
          const reason = treeAwareEffect.cancelled ? 'cancelled' : 'prevented'
        }
      } else {
        // Non-tree-aware effect - apply directly
        currentState = applyCardEffect(currentCard, activation, currentState)
      }
    }
  }

  // PHASE 3.25: Apply secondary effects from partiallyCounteredCards
  // These are damage cards whose damage was prevented by Hibernation but whose secondary effects should still run
  for (let i = currentState.activations.length - 1; i >= 0; i--) {
    const activation = currentState.activations[i]

    if (activation.partiallyCounteredCard) {
      const partialCard = activation.partiallyCounteredCard
      const cardType = getCardEffect(partialCard).type


      // Create a mock effectResult with damagePrevented=true
      // This ensures damage is skipped but secondary effects (like Chromabow choice) still run
      const mockEffectResult: EffectResult = {
        activationIndex: i,
        cardType,
        ownerId: partialCard.card.ownerId || activation.initiatorId,
        targetId: activation.targetId || (partialCard.card.ownerId === 1 ? 2 : 1),
        affectedChains: [],
        effectType: 'DAMAGE',
        cancelled: false,
        prevented: true,
        damagePrevented: true  // This ensures applyCardEffect skips damage but runs secondary effects
      }

      currentState = applyCardEffect(partialCard, activation, currentState, mockEffectResult)
    }
  }

  // PHASE 3.5: Apply reflected damage from Counterblast
  // Reflected damage EffectResults were created in applyEffectPriorities but aren't tied to activations
  // Skip both cancelled and prevented reflected damage
  const reflectedEffects = effectResults.filter(e =>
    e.cardType === CardType.Counterblast && e.effectType === 'DAMAGE' && !e.cancelled && !e.prevented
  )

  if (reflectedEffects.length > 0) {

    reflectedEffects.forEach(reflected => {
      const target = currentState.players.find(p => p.id === reflected.targetId)!
      const source = currentState.players.find(p => p.id === reflected.ownerId)!
      const reflectedDamage = reflected.damage || 0


      // CRITICAL FIX: Check if target is immune to reflected damage
      const targetIsImmune = isImmuneToDamage(target, currentState.round)

      if (targetIsImmune) {
        currentState = {
          ...currentState,
          log: [...currentState.log, `${target.name}'s immunity prevents ${reflectedDamage} reflected damage from ${source.name}'s Counterblast!`]
        }
        return
      }

      // Apply damage through shield first
      const damage = Math.max(0, reflectedDamage - target.shield)
      const damageLogs = applyHealthLoss(target, damage, source)
      target.shield = Math.max(0, target.shield - reflectedDamage)

      currentState = {
        ...currentState,
        players: [...currentState.players] as [Player, Player],
        log: [...currentState.log, ...damageLogs, `${source.name}'s Counterblast reflects ${reflectedDamage} damage to ${target.name}!`]
      }

    })
  }

  // PHASE 3: Post-resolution checks
  const [p1, p2] = currentState.players

  // Check for tower victory
  let towerVictor: Player | null = null
  if (p1.towers.length >= 5) {
    towerVictor = p1
    currentState.log.push(`${p1.name} has captured 5 towers and wins!`)
  } else if (p2.towers.length >= 5) {
    towerVictor = p2
    currentState.log.push(`${p2.name} has captured 5 towers and wins!`)
  }

  // LIFE CHARGER AUTO-TRIGGER CHECK
  // If a player is at 0 or below health and has Life Charger prepared with enough Essence,
  // create a new activation chain that opponent can counter (e.g., with Psy-Block).
  if (!towerVictor && (p1.health <= 0 || p2.health <= 0)) {
    for (let i = 0; i < currentState.players.length; i++) {
      const player = currentState.players[i]
      if (player.health <= 0) {
        // Find Life Charger in prepared
        const lifeChargerIndex = player.prepared.findIndex(pc => {
          const effect = getCardEffect(pc)
          return effect.type === CardType.LifeCharger
        })

        if (lifeChargerIndex !== -1) {
          const lifeChargerCard = player.prepared[lifeChargerIndex]
          // Apply Occultist discount (50% off Artifacts)
          const rawCost = lifeChargerCard.card.essenceCost || 0
          const essenceCost = (rawCost && lifeChargerCard.card.subType === 'Artifact' && player.playerClass === 'OCCULTIST') ? Math.floor(rawCost / 2) : rawCost

          if (player.essence >= essenceCost) {

            // Create Life Charger activation that can be countered
            const lifeChargerActivation: ActivationAttempt = {
              initiatorId: player.id,
              attackingCard: lifeChargerCard,
              counterChain: [],
              state: 'PENDING_RESPONSE',
              targetId: player.id, // Targets self
              resolved: false,
              accepted: false,
              parentActivationIndex: undefined,
              childActivationIndices: [],
              treeDepth: 0
            }

            // Deduct essence
            player.essence -= essenceCost

            // Remove from prepared (will be added to hand if successful)
            player.prepared.splice(lifeChargerIndex, 1)

            currentState.log.push(`t:log.lifeChargerAuto|playerName=${player.name}`)
            currentState.log.push(`t:log.payEssence|playerName=${player.name}|essence=${essenceCost}`)

            // Opponent must respond to this activation
            const opponentId = player.id === 1 ? 2 : 1

            return {
              ...currentState,
              phase: GamePhase.LifeChargerPending,
              pendingLifeChargerPlayerId: player.id,
              activations: [lifeChargerActivation],
              acceptedActivations: [],
              lastCardPlayerId: player.id, // The player who auto-played Life Charger
              turnPlayerId: opponentId // Opponent's turn to respond
            }
          } else {
          }
        }
      }
    }
  }

  // Check for game-over condition (health <= 0 or tower victory)
  const [fp1, fp2] = currentState.players // Fresh references after all effects
  if (fp1.health <= 0 || fp2.health <= 0 || towerVictor) {
    let winner: Player | null = null

    if (towerVictor) {
      winner = towerVictor
    } else if (fp1.health <= 0 && fp2.health <= 0) {
      currentState.log.push("It's a draw!")
      winner = null
    } else if (fp1.health <= 0) {
      winner = fp2
      currentState.log.push(`${fp2.name} is victorious!`)
    } else {
      winner = fp1
      currentState.log.push(`${fp1.name} is victorious!`)
    }

    // Fame calculation for winner
    let lastFameDelta = 0
    let playersCopy: [Player, Player] = [fp1, fp2]
    // Only calculate Fame Points for PvP games (not AI)
    const isAIGame = fp1.isAI || fp2.isAI
    if (winner && !isAIGame) {
      const today = new Date()
      const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
      const loser = winner.id === fp1.id ? fp2 : fp1
      const newHistoryEntry: DetailedMatchResult = {
        id: currentState.round,
        date: dateStr,
        winnerId: winner.id,
        loserId: loser.id,
        opponentName: loser.name,
        result: 'win',
        fameDelta: 0 // Will be updated after calculation
      }

      // Use PRIOR history for fame calculation (before adding current game)
      let battleHistory = currentState.battleHistory ? [...currentState.battleHistory] : []
      const priorHistory = [...battleHistory] // Copy for calculation
      // Convert DetailedMatchResult[] to FHGame[] for calculateFamePoints
      const priorHistoryAsFHGame: FHGame[] = priorHistory
        .filter(g => g.winnerId !== undefined)
        .map(g => ({ id: g.id, date: g.date, winnerId: g.winnerId!, loserId: g.loserId }))

      const w = winner.id === p1.id ? p1 : p2
      const gamesP1Today = priorHistoryAsFHGame.filter(g => g.winnerId === p1.id && g.date === dateStr)
      const gamesP2Today = priorHistoryAsFHGame.filter(g => g.winnerId === p2.id && g.date === dateStr)

      const calc = calculateFamePoints({
        player1: { id: p1.id, famePoints: p1.famePoints || 0 },
        player2: { id: p2.id, famePoints: p2.famePoints || 0 },
        winner: { id: w.id, famePoints: w.famePoints || 0 },
        gamesOfBattleHistory: priorHistoryAsFHGame,
        gamesOfPlayer1Today: gamesP1Today,
        gamesOfPlayer2Today: gamesP2Today,
        isInAuthorizedOfflinePlace: false,
        isRecommendedBySystem: false,
      })

      // NOW add the current game to history (after calculation)
      battleHistory.push(newHistoryEntry)

      const wi = playersCopy.findIndex(p => p.id === winner!.id)
      playersCopy[wi] = { ...playersCopy[wi], famePoints: calc.newFamePoints }
      winner = playersCopy[wi] // Update winner reference to the one with new famePoints
      currentState.log.push(`${playersCopy[wi].name} gains ${calc.paramR} fame points.`)
      lastFameDelta = calc.paramR
      console.log(`[Fame] finalResolution Calculated: paramB=${calc.paramB}, paramG=${calc.paramG}, paramR=${calc.paramR}, newFamePoints=${calc.newFamePoints}, winsVsOpponent=${calc.gamesOfBattleHistoryCount}`)

      try {
        const enriched = battleHistory.map(g => ({ ...g, player1Name: playersCopy[0].name, player2Name: playersCopy[1].name }))
        localStorage.setItem('battleHistory', JSON.stringify(enriched))
      } catch { }
    }

    const pendingVictory = winner ? { winnerId: winner.id } : null


    return {
      ...currentState,
      players: playersCopy,
      phase: GamePhase.GameOver,
      winner,
      lastFameDelta,
      pendingVictory,
      activations: [],
      acceptedActivations: []
    }
  }


  // Clear activations after final resolution
  return {
    ...currentState,
    activations: [],
    acceptedActivations: []
  }
}

const endOfCombatRoutine = (state: GameState): GameState => {
  let playersCopy: [Player, Player] = JSON.parse(JSON.stringify(state.players))
  let [p1, p2] = playersCopy
  let newLog = [...state.log, `The night ends.`]
  let winner = null
  let phase = GamePhase.RoundEnd
  for (let i = 0; i < playersCopy.length; i++) {
    const player = playersCopy[i]
    if (player.health <= 0) {
      const stateBeforeCheck = { ...state, players: playersCopy, log: newLog }
      const stateAfterCheck = checkAndTriggerLifeCharger(stateBeforeCheck, player.id)
      if (stateAfterCheck.players[i].health > 0) {
        playersCopy = stateAfterCheck.players
        newLog = stateAfterCheck.log
      }
    }
  }
  ;[p1, p2] = playersCopy
  const [hp1, hp2] = playersCopy // Use fresh references after Life Charger checks
  if (hp1.health <= 0 || hp2.health <= 0) {
    if (hp1.health <= 0 && hp2.health <= 0) {
      winner = null
      newLog.push("It's a draw!")
    } else if (hp1.health <= 0) {
      winner = hp2
      newLog.push(`${hp2.name} is victorious!`)
    } else {
      winner = hp1
      newLog.push(`${hp1.name} is victorious!`)
    }
    phase = GamePhase.GameOver
  }
  const allPreparedCards = [...p1.prepared, ...p2.prepared]
  allPreparedCards.forEach(pc => {
    const owner = playersCopy.find(p => p.id === pc.card.ownerId)
    // Skip Life Charger - keep it prepared for Disease/NaturalGrip check in transitionToPreparationPhase
    const cardType = getCardEffect(pc).type
    if (cardType === CardType.LifeCharger) {
      return // Don't unprepare Life Charger
    }
    if (owner && !pc.lockedState) {
      owner.hand.push(pc.card)
    }
  })
  // Keep Life Charger in prepared, filter out other non-locked cards
  p1.prepared = p1.prepared.filter(pc => pc.lockedState || getCardEffect(pc).type === CardType.LifeCharger)
  p2.prepared = p2.prepared.filter(pc => pc.lockedState || getCardEffect(pc).type === CardType.LifeCharger)
  p1.resolved = []
  p2.resolved = []

  // Fame calculation for winner
  let lastFameDelta = 0
  // Only calculate Fame Points for PvP games (not AI)
  const isAIGame = p1.isAI || p2.isAI
  if (winner && !isAIGame) {
    const today = new Date()
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    const loser = winner.id === p1.id ? p2 : p1
    const newHistoryEntry: DetailedMatchResult = {
      id: state.round,
      date: dateStr,
      winnerId: winner.id,
      loserId: loser.id,
      opponentName: loser.name,
      result: 'win',
      fameDelta: 0 // Will be updated after calculation
    }

    // Use PRIOR history for fame calculation (before adding current game)
    let battleHistory = state.battleHistory ? [...state.battleHistory] : []
    const priorHistory = [...battleHistory] // Copy for calculation
    // Convert DetailedMatchResult[] to FHGame[] for calculateFamePoints
    const priorHistoryAsFHGame: FHGame[] = priorHistory
      .filter(g => g.winnerId !== undefined)
      .map(g => ({ id: g.id, date: g.date, winnerId: g.winnerId!, loserId: g.loserId }))

    const w = winner.id === p1.id ? p1 : p2
    const gamesP1Today = priorHistoryAsFHGame.filter(g => g.winnerId === p1.id && g.date === dateStr)
    const gamesP2Today = priorHistoryAsFHGame.filter(g => g.winnerId === p2.id && g.date === dateStr)

    const calc = calculateFamePoints({
      player1: { id: p1.id, famePoints: p1.famePoints || 0 },
      player2: { id: p2.id, famePoints: p2.famePoints || 0 },
      winner: { id: w.id, famePoints: w.famePoints || 0 },
      gamesOfBattleHistory: priorHistoryAsFHGame,
      gamesOfPlayer1Today: gamesP1Today,
      gamesOfPlayer2Today: gamesP2Today,
      isInAuthorizedOfflinePlace: false,
      isRecommendedBySystem: false,
    })

    // Update fameDelta in history entry
    newHistoryEntry.fameDelta = calc.paramR

    // NOW add the current game to history (after calculation)
    battleHistory.push(newHistoryEntry)

    const wi = playersCopy.findIndex(p => p.id === winner!.id)
    playersCopy[wi] = { ...playersCopy[wi], famePoints: calc.newFamePoints }
    winner = playersCopy[wi] // Update winner reference to the one with new famePoints
    newLog.push(`${playersCopy[wi].name} gains ${calc.paramR} fame points.`)
    lastFameDelta = calc.paramR
    console.log(`[Fame] activations.ts endOfCombatRoutine Calculated: paramB=${calc.paramB}, paramG=${calc.paramG}, paramR=${calc.paramR}`)

    try {
      const enriched = battleHistory.map(g => ({ ...g, player1Name: playersCopy[0].name, player2Name: playersCopy[1].name }))
      localStorage.setItem('battleHistory', JSON.stringify(enriched))
    } catch { }
  }

  const pendingVictory = winner ? { winnerId: winner.id } : null
  return { ...state, players: playersCopy, log: newLog, phase, winner, lastFameDelta, drawEvents: state.drawEvents, discardMode: {}, activations: [], pendingVictory }
}

export const passTurn = (state: GameState, playerId: number): GameState => {
  if (state.turnPlayerId !== playerId || state.activations.length > 0) return state
  const playerIndex = state.players.findIndex(p => p.id === playerId)
  if (playerIndex === -1) return state
  const newPlayers = [...state.players] as [Player, Player]
  const player = newPlayers[playerIndex]
  if (!player.hasPassedActiveTurn) {
    newPlayers[playerIndex] = { ...player, hasPassedActiveTurn: true }
  }
  const newConsecutivePasses = state.consecutivePasses + 1
  if (newConsecutivePasses >= 2) {
    return endOfCombatRoutine({ ...state, players: newPlayers })
  }
  const nextPlayerId = playerId === 1 ? 2 : 1
  return { ...state, players: newPlayers, turnPlayerId: nextPlayerId, consecutivePasses: newConsecutivePasses, discardMode: {}, log: [...state.log, `${state.players.find(p => p.id === playerId)!.name} passes.`] }
}

export const astridHeroBlast = (state: GameState, playerId: number, targetId: number, color: CardColor): GameState => {
  if (state.activations.length > 0) return state;
  const playerIndex = state.players.findIndex(p => p.id === playerId);
  const targetIndex = state.players.findIndex(p => p.id === targetId);
  if (playerIndex === -1 || targetIndex === -1) return state;
  const newPlayers = [...state.players] as [Player, Player];
  const attacker = { ...newPlayers[playerIndex] };
  const defender = { ...newPlayers[targetIndex] };
  if (attacker.hero !== 'ASTRID') return state;
  const charges = attacker.astridCharges?.[color] || 0;
  if (charges < 3) return state;
  attacker.astridCharges = { ...(attacker.astridCharges || {}), [color]: charges - 3 };
  const damageLogs = applyHealthLoss(defender, 2, attacker);
  newPlayers[playerIndex] = attacker;
  newPlayers[targetIndex] = defender;
  const newLog = [...state.log, `${attacker.name}'s Astrid unleashes unavoidable ${color} blast for 2 damage on ${defender.name}.`, ...damageLogs, `[REWARD] HERO_POWER_USED:PLAYER=${playerId}:TYPE=ASTRID_BLAST`];
  return { ...state, players: newPlayers, log: newLog };
};
